<template>
  <div
    v-if="isOpen"
    ref="modalRef"
    class="stats-modal"
    :style="modal.modalStyle.value"
    @mousedown="bringToFront"
  >
    <!-- Header (draggable) -->
    <div
      ref="headerRef"
      class="modal-header"
      :class="{ flashing: modal.isFlashing.value }"
      @mousedown="onStartDrag"
    >
      <span class="modal-title">Network Statistics</span>
      <button class="close-btn" @click="close" title="Close"><XMarkIcon class="close-icon" /></button>
    </div>

    <!-- Content -->
    <div class="modal-content">
      <div class="stats-grid">
        <!-- Block Info -->
        <div class="stat-row odd">
          <div class="stat-info">
            <span class="label">Block Number:</span>
            <span class="value">#{{ currentBlock.toLocaleString() }}</span>
          </div>
          <button class="pin-btn" @click="togglePin('block')" title="Pin to header">
            <PlusCircleIconSolid v-if="isPinned('block')" class="pin-icon pinned" />
            <PlusCircleIcon v-else class="pin-icon" />
          </button>
        </div>

        <div class="section-divider"></div>

        <!-- Performance Metrics -->
        <div class="stat-row odd">
          <div class="stat-info">
            <span class="label">TX/SEC:</span>
            <span class="value">{{ formattedTps }}</span>
          </div>
          <button class="pin-btn" @click="togglePin('tps')" title="Pin to header">
            <PlusCircleIconSolid v-if="isPinned('tps')" class="pin-icon pinned" />
            <PlusCircleIcon v-else class="pin-icon" />
          </button>
        </div>

        <div class="stat-row even">
          <div class="stat-info">
            <span class="label">GAS/SEC:</span>
            <span class="value">{{ formattedGps }}</span>
          </div>
          <button class="pin-btn" @click="togglePin('gps')" title="Pin to header">
            <PlusCircleIconSolid v-if="isPinned('gps')" class="pin-icon pinned" />
            <PlusCircleIcon v-else class="pin-icon" />
          </button>
        </div>

        <div class="stat-row odd">
          <div class="stat-info">
            <span class="label">Megablocks/sec:</span>
            <span class="value">{{ formattedMbps }}</span>
          </div>
          <button class="pin-btn" @click="togglePin('mbps')" title="Pin to header">
            <PlusCircleIconSolid v-if="isPinned('mbps')" class="pin-icon pinned" />
            <PlusCircleIcon v-else class="pin-icon" />
          </button>
        </div>

        <div class="stat-row even">
          <div class="stat-info">
            <span class="label">Blocks/sec:</span>
            <span class="value">{{ formattedBps }}</span>
          </div>
          <button class="pin-btn" @click="togglePin('bps')" title="Pin to header">
            <PlusCircleIconSolid v-if="isPinned('bps')" class="pin-icon pinned" />
            <PlusCircleIcon v-else class="pin-icon" />
          </button>
        </div>

        <div class="stat-row odd">
          <div class="stat-info">
            <span class="label">Frontend BPS:</span>
            <span class="value">{{ formattedFbps }}</span>
          </div>
          <button class="pin-btn" @click="togglePin('fbps')" title="Pin to header">
            <PlusCircleIconSolid v-if="isPinned('fbps')" class="pin-icon pinned" />
            <PlusCircleIcon v-else class="pin-icon" />
          </button>
        </div>

        <div class="stat-row even">
          <div class="stat-info">
            <span class="label">Backend BPS:</span>
            <span class="value">{{ formattedBebps }}</span>
          </div>
          <button class="pin-btn" @click="togglePin('bebps')" title="Pin to header">
            <PlusCircleIconSolid v-if="isPinned('bebps')" class="pin-icon pinned" />
            <PlusCircleIcon v-else class="pin-icon" />
          </button>
        </div>

        <div class="stat-row odd">
          <div class="stat-info">
            <span class="label">Upstream BPS:</span>
            <span class="value">{{ formattedUbps }}</span>
          </div>
          <button class="pin-btn" @click="togglePin('ubps')" title="Pin to header">
            <PlusCircleIconSolid v-if="isPinned('ubps')" class="pin-icon pinned" />
            <PlusCircleIcon v-else class="pin-icon" />
          </button>
        </div>

        <div class="stat-row even">
          <div class="stat-info">
            <span class="label">FPS:</span>
            <span class="value">{{ formattedFps }}</span>
          </div>
          <button class="pin-btn" @click="togglePin('fps')" title="Pin to header">
            <PlusCircleIconSolid v-if="isPinned('fps')" class="pin-icon pinned" />
            <PlusCircleIcon v-else class="pin-icon" />
          </button>
        </div>
      </div>
    </div>

    <!-- Resize Handles -->
    <div class="resize-handle resize-right" @mousedown.stop="(e) => modal.startResize('right', e)"></div>
    <div class="resize-handle resize-bottom" @mousedown.stop="(e) => modal.startResize('bottom', e)"></div>
    <div class="resize-handle resize-corner" @mousedown.stop="(e) => modal.startResize('corner', e)"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue';
import { XMarkIcon, PlusCircleIcon, PlusCircleIconSolid } from '@/components/icons';
import type { BlockchainState } from '@/stores/blockchainState';
import { userSettings } from '@/stores/userSettings';
import { useModalDragResize } from '@/composables/useModalDragResize';
import { formatNumber } from '@/utils/formatters';

// Props
export interface StatsModalProps {
  zIndexBase?: number;
  state: BlockchainState;
}

const props = withDefaults(defineProps<StatsModalProps>(), {
  zIndexBase: 1000
});

// Emits
const emit = defineEmits<{
  close: [];
  focus: [];
}>();

// Modal drag/resize composable
const modal = useModalDragResize({
  initialWidth: 320,
  initialHeight: 480,
  minWidth: 250,
  minHeight: 300,
  zIndexBase: props.zIndexBase
});

// State
const isOpen = ref(false);
const headerRef = ref<HTMLElement>();

// Stats update interval
let statsUpdateInterval: number | null = null;

// Reactive stats
const currentBlock = ref(0);
const tps = ref(0);
const gasPerSec = ref(0);
const miniblocksPerSec = ref(0);
const blocksPerSec = ref(0);
const frontendBps = ref(0);
const backendBpsValue = ref(0);
const upstreamBps = ref(0);
const fps = ref(0);

// Pinned states
const pinnedStates = ref<Record<string, boolean>>({});

const updatePinnedStates = () => {
  pinnedStates.value = {
    block: userSettings.isPinned('block'),
    tps: userSettings.isPinned('tps'),
    gps: userSettings.isPinned('gps'),
    mbps: userSettings.isPinned('mbps'),
    bps: userSettings.isPinned('bps'),
    fbps: userSettings.isPinned('fbps'),
    bebps: userSettings.isPinned('bebps'),
    ubps: userSettings.isPinned('ubps'),
    fps: userSettings.isPinned('fps'),
  };
};

const togglePin = (key: string) => {
  userSettings.togglePin(key);
  updatePinnedStates();
};

const isPinned = (key: string): boolean => {
  return pinnedStates.value[key] || false;
};

const unsubscribe = userSettings.subscribe(() => {
  updatePinnedStates();
});

// Computed formatted values
const formattedTps = computed(() => formatNumber(tps.value));
const formattedGps = computed(() => formatNumber(gasPerSec.value));
const formattedMbps = computed(() => formatNumber(miniblocksPerSec.value));
const formattedBps = computed(() => formatNumber(blocksPerSec.value));
const formattedFbps = computed(() => formatNumber(frontendBps.value));
const formattedBebps = computed(() => formatNumber(backendBpsValue.value));
const formattedUbps = computed(() => formatNumber(upstreamBps.value));
const formattedFps = computed(() => Math.round(fps.value).toString());

const updateStats = () => {
  currentBlock.value = props.state.currentBlock.value;
  tps.value = props.state.tps.value;
  gasPerSec.value = props.state.gasPerSec.value;
  miniblocksPerSec.value = props.state.miniblocksPerSec.value;
  blocksPerSec.value = props.state.blocksPerSec.value;
  frontendBps.value = props.state.frontendBps.value;
  backendBpsValue.value = props.state.backendBpsValue.value;
  upstreamBps.value = props.state.upstreamBps.value;
  fps.value = props.state.fps.value;
};

const onStartDrag = (e: MouseEvent) => {
  modal.startDrag(e, headerRef.value);
};

const open = (mouseX: number, mouseY: number) => {
  modal.positionNear(mouseX, mouseY);
  isOpen.value = true;

  updateStats();
  updatePinnedStates();
  if (statsUpdateInterval !== null) {
    clearInterval(statsUpdateInterval);
  }
  statsUpdateInterval = window.setInterval(updateStats, 200);

  modal.flashHeader();
};

const close = () => {
  isOpen.value = false;
  if (statsUpdateInterval !== null) {
    clearInterval(statsUpdateInterval);
    statsUpdateInterval = null;
  }
  emit('close');
};

const bringToFront = () => {
  emit('focus');
};

onUnmounted(() => {
  if (statsUpdateInterval !== null) {
    clearInterval(statsUpdateInterval);
    statsUpdateInterval = null;
  }
  unsubscribe();
});

defineExpose({
  open,
  close,
  setZIndex: modal.setZIndex,
  flashHeader: modal.flashHeader,
  isOpen: () => isOpen.value
});
</script>

<style scoped>
.stats-modal {
  position: fixed;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  font-family: Inter, Arial, sans-serif;
  user-select: none;
  box-shadow: var(--shadow-lg);
  border-radius: 4px;
}

.modal-header {
  height: 40px;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 16px;
  cursor: move;
  flex-shrink: 0;
  transition: background 0.3s;
  position: relative;
  border-radius: 4px 4px 0 0;
}

.modal-header.flashing {
  animation: headerFlash 500ms ease-out;
}

@keyframes headerFlash {
  0% { background: var(--accent-primary); opacity: 0.3; }
  100% { background: var(--bg-elevated); }
}

.modal-title {
  color: var(--accent-primary);
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.close-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: 1px solid var(--error);
  color: var(--error);
  width: 24px;
  height: 24px;
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
}

.close-btn:hover {
  background: var(--error);
  color: var(--text-inverse);
}

.close-icon {
  width: 14px;
  height: 14px;
}

.modal-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  color: var(--text-primary);
  min-height: 0;
}

.stats-grid {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  margin: 0 -12px;
}

.stat-row.odd { background: var(--hover-overlay); }
.stat-row.even { background: transparent; }

.stat-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
}

.label {
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.value {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  text-align: right;
}

.pin-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pin-icon {
  width: 18px;
  height: 18px;
  color: var(--text-muted);
  transition: color 0.2s;
}

.pin-btn:hover .pin-icon { color: var(--accent-primary); }
.pin-icon.pinned { color: var(--accent-primary); }

.section-divider {
  height: 1px;
  background: var(--border);
  margin: 8px 0;
}

.resize-handle {
  position: absolute;
  background: transparent;
}

.resize-right {
  right: 0;
  top: 0;
  width: 8px;
  height: 100%;
  cursor: ew-resize;
}

.resize-bottom {
  bottom: 0;
  left: 0;
  width: 100%;
  height: 8px;
  cursor: ns-resize;
}

.resize-corner {
  right: 0;
  bottom: 0;
  width: 16px;
  height: 16px;
  cursor: nwse-resize;
  background: var(--accent-primary);
  clip-path: polygon(100% 0, 100% 100%, 0 100%);
}

.resize-corner:hover { background: var(--success); }

.modal-content::-webkit-scrollbar { width: 8px; }
.modal-content::-webkit-scrollbar-track { background: var(--bg-primary); }
.modal-content::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }
.modal-content::-webkit-scrollbar-thumb:hover { background: var(--accent-primary); }
</style>
